import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import ComponentCard from "../../components/common/ComponentCard";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import Button from "../../components/ui/button/Button";
import api from "../../services/api";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Plus, Trash2 } from "lucide-react";
import Select from "../../components/form/Select";

interface ProductOption {
    id: string;
    name: string;
    offers?: { id: string; name: string }[];
}

interface Assignment {
    productId: string;
    offerId: string;
}

const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["blockquote", "link"],
        ["clean"],
    ],
};

const quillFormats = [
    "header",
    "bold", "italic", "underline",
    "list",
    "blockquote", "link",
];

export default function EditTerm() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [formData, setFormData] = useState({ title: "", content: "" });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [currentVersion, setCurrentVersion] = useState<number>(0);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState("");
    const [notificationType, setNotificationType] = useState<"success" | "error">("success");

    // Assignments
    const [assignmentType, setAssignmentType] = useState<string>("global");
    const [assignments, setAssignments] = useState<Assignment[]>([{ productId: "", offerId: "" }]);
    const [products, setProducts] = useState<ProductOption[]>([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [assignmentsChanged, setAssignmentsChanged] = useState(false);

    const displayNotification = (message: string, type: "success" | "error") => {
        setNotificationMessage(message);
        setNotificationType(type);
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 5000);
    };

    // Buscar produtos (usando /api/products que retorna id como UUID)
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setProductsLoading(true);
                const response = await api.get("/api/products");
                if (response.data && response.data.products) {
                    const prods = response.data.products.map((p: { id: string; name: string }) => ({
                        id: p.id,
                        name: p.name,
                        offers: [] as { id: string; name: string }[]
                    }));
                    setProducts(prods);
                }
            } catch (err: unknown) {
                console.error("Erro ao buscar produtos:", err);
            } finally {
                setProductsLoading(false);
            }
        };
        fetchProducts();
    }, []);

    // Buscar dados do termo
    useEffect(() => {
        const fetchTerm = async () => {
            try {
                setPageLoading(true);
                const response = await api.get(`/api/company-terms/${id}`);

                if (response.data && response.data.success) {
                    const term = response.data.terms;
                    setFormData({
                        title: term.title || "",
                        content: term.content || ""
                    });
                    setCurrentVersion(term.version || 0);

                    // Carregar assignments existentes
                    const existingAssignments = term.companyTermsAssignment || [];
                    if (existingAssignments.length === 0) {
                        setAssignmentType("global");
                        setAssignments([{ productId: "", offerId: "" }]);
                    } else {
                        const hasGlobal = existingAssignments.some((a: { productId: string | null }) => !a.productId);
                        if (hasGlobal) {
                            setAssignmentType("global");
                            setAssignments([{ productId: "", offerId: "" }]);
                        } else {
                            setAssignmentType("specific");
                            const mapped = existingAssignments.map((a: { productId: string; offerId: string | null }) => ({
                                productId: a.productId || "",
                                offerId: a.offerId || ""
                            }));
                            setAssignments(mapped);

                            // Buscar ofertas dos produtos ja associados
                            const uniqueProductIds = [...new Set(mapped.map((a: Assignment) => a.productId).filter(Boolean))];
                            for (const pid of uniqueProductIds) {
                                try {
                                    const offersRes = await api.get(`/api/products/${pid}/offers-with-links`);
                                    if (offersRes.data && offersRes.data.offers) {
                                        const offers = offersRes.data.offers.map((o: { id: string; name: string }) => ({
                                            id: o.id,
                                            name: o.name
                                        }));
                                        setProducts(prev => prev.map(p =>
                                            p.id === pid ? { ...p, offers } : p
                                        ));
                                    }
                                } catch {
                                    // silenciar erro de ofertas individuais
                                }
                            }
                        }
                    }
                }
            } catch (err: unknown) {
                console.error("Erro ao buscar termo:", err);
                displayNotification("Erro ao carregar termo de uso.", "error");
            } finally {
                setPageLoading(false);
            }
        };

        if (id) fetchTerm();
    }, [id]);

    const validateForm = () => {
        const errors: Record<string, string> = {};
        if (!formData.title.trim()) errors.title = "Titulo e obrigatorio";
        const strippedContent = formData.content.replace(/<(.|\n)*?>/g, "").trim();
        if (!strippedContent) errors.content = "Conteudo e obrigatorio";

        if (assignmentType === "specific") {
            const hasValid = assignments.some(a => a.productId);
            if (!hasValid) errors.assignments = "Selecione pelo menos um produto";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const buildAssignments = () => {
        if (assignmentType === "global") {
            return [{}];
        }
        return assignments
            .filter(a => a.productId)
            .map(a => {
                const item: { productId: string; offerId?: string } = { productId: a.productId };
                if (a.offerId) item.offerId = a.offerId;
                return item;
            });
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);

            // Atualizar titulo/conteudo (cria nova versao)
            const response = await api.put(`/api/company-terms/${id}`, {
                title: formData.title,
                content: formData.content
            });

            if (response.data && response.data.success) {
                // Se assignments foram alterados, atualizar separadamente
                if (assignmentsChanged) {
                    const newTermId = response.data.terms?.id || id;
                    await api.put(`/api/company-terms/${newTermId}/assignments`, {
                        assignments: buildAssignments()
                    });
                }

                displayNotification("Termos atualizados com sucesso! Nova versao criada.", "success");
                setTimeout(() => {
                    navigate("/products?tab=terms");
                }, 1000);
            }
        } catch (err: unknown) {
            console.error("Erro ao atualizar termos:", err);
            const axiosErr = err as { response?: { data?: { message?: string } } };
            const message = axiosErr.response?.data?.message || "Erro ao atualizar termos de uso.";
            displayNotification(message, "error");
        } finally {
            setLoading(false);
        }
    };

    // Gerenciar assignments
    const addAssignment = () => {
        setAssignments([...assignments, { productId: "", offerId: "" }]);
        setAssignmentsChanged(true);
    };

    const removeAssignment = (index: number) => {
        setAssignments(assignments.filter((_, i) => i !== index));
        setAssignmentsChanged(true);
    };

    const updateAssignment = (index: number, field: "productId" | "offerId", value: string) => {
        const updated = [...assignments];
        updated[index] = { ...updated[index], [field]: value };
        if (field === "productId") {
            updated[index].offerId = "";
            if (value) {
                fetchOffersForProduct(value);
            }
        }
        setAssignments(updated);
        setAssignmentsChanged(true);
    };

    const fetchOffersForProduct = async (productId: string) => {
        const existing = products.find(p => p.id === productId);
        if (existing && existing.offers && existing.offers.length > 0) return;

        try {
            const response = await api.get(`/api/products/${productId}/offers-with-links`);
            if (response.data && response.data.offers) {
                const offers = response.data.offers.map((o: { id: string; name: string }) => ({
                    id: o.id,
                    name: o.name
                }));
                setProducts(prev => prev.map(p =>
                    p.id === productId ? { ...p, offers } : p
                ));
            }
        } catch (err: unknown) {
            console.error("Erro ao buscar ofertas:", err);
        }
    };

    const handleAssignmentTypeChange = (type: string) => {
        setAssignmentType(type);
        setAssignmentsChanged(true);
        if (type === "specific" && assignments.length === 0) {
            setAssignments([{ productId: "", offerId: "" }]);
        }
    };

    const getOffersForProduct = (productId: string) => {
        const product = products.find(p => p.id === productId);
        return product?.offers || [];
    };

    if (pageLoading) {
        return (
            <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
            </div>
        );
    }

    return (
        <div>
            <PageMeta
                title="Editar Termo de Uso | Sellyo"
                description="Editar termo de uso"
            />
            <PageBreadcrumb
                pageTitle="Editar Termo de Uso"
                showBackButton={true}
                onBack={() => navigate("/products?tab=terms")}
            />

            <div className="space-y-6">
                <ComponentCard title="Editar Termo de Uso">
                    <div className="space-y-6">
                        {/* Info versao */}
                        <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3">
                            <p className="text-xs text-blue-600 dark:text-blue-400">
                                Versao atual: <strong>v{currentVersion}</strong> — Ao salvar, uma nova versao sera criada automaticamente. Aceites anteriores continuam vinculados a versao antiga.
                            </p>
                        </div>

                        <div>
                            <Label htmlFor="term-title">Titulo *</Label>
                            <Input
                                id="term-title"
                                placeholder="Ex: Termos de Uso - Meu Produto"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                error={!!formErrors.title}
                                hint={formErrors.title}
                            />
                        </div>

                        {/* Associacoes */}
                        <div>
                            <Label>Exigir no checkout de *</Label>
                            <div className="flex gap-4 mt-3">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="assignmentType"
                                        value="global"
                                        checked={assignmentType === "global"}
                                        onChange={() => handleAssignmentTypeChange("global")}
                                        className="h-4 w-4 text-brand-500 border-gray-300 focus:ring-brand-500"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">Todos os produtos</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="assignmentType"
                                        value="specific"
                                        checked={assignmentType === "specific"}
                                        onChange={() => handleAssignmentTypeChange("specific")}
                                        className="h-4 w-4 text-brand-500 border-gray-300 focus:ring-brand-500"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">Produtos especificos</span>
                                </label>
                            </div>

                            {assignmentType === "global" && (
                                <p className="mt-2 text-xs text-blue-500 dark:text-blue-400">
                                    Este termo de uso sera exibido em todos os checkouts de todos os produtos, incluindo os futuros.
                                </p>
                            )}

                            {assignmentType === "specific" && (
                                <div className="space-y-3 mt-4">
                                    {productsLoading ? (
                                        <div className="flex justify-center py-4">
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
                                        </div>
                                    ) : (
                                        <>
                                            {assignments.map((assignment, index) => {
                                                const offers = getOffersForProduct(assignment.productId);
                                                const productOptions = products.map(p => ({ value: p.id, label: p.name }));
                                                const offerOptions = [
                                                    { value: "", label: "Todas as ofertas" },
                                                    ...offers.map(o => ({ value: o.id, label: o.name }))
                                                ];

                                                return (
                                                    <div key={index} className="flex gap-3 items-start">
                                                        <div className="flex-1">
                                                            <Select
                                                                options={productOptions}
                                                                placeholder="Selecione um produto"
                                                                value={assignment.productId}
                                                                onChange={(value) => updateAssignment(index, "productId", value)}
                                                            />
                                                        </div>
                                                        {assignment.productId && offers.length > 0 && (
                                                            <div className="flex-1">
                                                                <Select
                                                                    options={offerOptions}
                                                                    placeholder="Todas as ofertas"
                                                                    value={assignment.offerId}
                                                                    onChange={(value) => updateAssignment(index, "offerId", value)}
                                                                />
                                                            </div>
                                                        )}
                                                        {assignments.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeAssignment(index)}
                                                                className="mt-1.5 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                            >
                                                                <Trash2 className="size-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })}

                                            <button
                                                type="button"
                                                onClick={addAssignment}
                                                className="flex items-center gap-2 text-sm text-brand-500 hover:text-brand-600 font-medium"
                                            >
                                                <Plus className="size-4" />
                                                Adicionar outro produto
                                            </button>

                                            {formErrors.assignments && (
                                                <p className="text-xs text-error-500">{formErrors.assignments}</p>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        <div>
                            <Label>Conteudo *</Label>
                            <div className="quill-terms-editor">
                                <ReactQuill
                                    theme="snow"
                                    value={formData.content}
                                    onChange={(value) => setFormData({ ...formData, content: value })}
                                    modules={quillModules}
                                    formats={quillFormats}
                                    placeholder="Escreva os termos de uso aqui..."
                                />
                            </div>
                            {formErrors.content && (
                                <p className="mt-1.5 text-xs text-error-500">{formErrors.content}</p>
                            )}
                        </div>
                    </div>
                </ComponentCard>

                {/* Botoes */}
                <div className="flex justify-end gap-3">
                    <Button
                        variant="outline"
                        size="md"
                        onClick={() => navigate("/products?tab=terms")}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? "Salvando..." : "Salvar (Nova Versao)"}
                    </Button>
                </div>
            </div>

            {/* Notificacao */}
            {showNotification && (
                <div className={`fixed top-4 right-4 z-50 rounded-md p-4 shadow-md transition-all duration-300 ${notificationType === "success"
                    ? "bg-success-500 dark:bg-success-400 text-white"
                    : "bg-danger-500 dark:bg-danger-400 text-white"
                    }`}>
                    <div className="flex items-center">
                        {notificationType === "success" ? (
                            <svg className="h-5 w-5 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        ) : (
                            <svg className="h-5 w-5 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        )}
                        <span>{notificationMessage}</span>
                    </div>
                </div>
            )}

            {/* Estilo para aumentar a area do editor */}
            <style>{`
                .quill-terms-editor .ql-container {
                    min-height: 350px;
                    font-size: 14px;
                }
                .quill-terms-editor .ql-editor {
                    min-height: 350px;
                }
                .quill-terms-editor .ql-editor.ql-blank::before {
                    font-style: italic;
                    color: #9ca3af;
                }
            `}</style>
        </div>
    );
}
